// src/oura/businesses/FocusPanels.tsx
//
// The top of the Businesses list (Jack, 5 Nov 2026): "Start here", every
// client that needs something, worst first, with the one thing to do; beside
// it where every client's scores sit on one 0-100 line, and how their
// Delivery is going (the Tracker's own read). The table below stays the
// full list.

import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, CheckCircle2Icon, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { FOCUS_RING, LogoSquare, SURFACE, Tag, TYPE, type Tone } from "@oura/ui";
import type { View } from "../nav";
import { OURA_ROOT } from "../nav";
import { band, type Focus } from "./focus";
import { HEALTH, type ClientCard, type Health } from "../board/model";

export interface FocusClient {
    id: string;
    name: string;
    logo: string | null;
    leadTone: Tone;
    leadIcon: LucideIcon;
    focuses: Focus[];
}

function Why({ f }: { f: Focus }) {
    return (
        <span className={cn(TYPE.tc, f.late ? "font-medium text-rose-600" : "text-gray-500")}>{f.why}</span>
    );
}

/* ------------------------------------------------------------------------- *
 * Start here
 * ------------------------------------------------------------------------- */

const SHOW = 5;

/**
 * Whether the Delivery the ranking leans on is known. "off": Delivery is not on in this build, so
 * late work is not a reason at all. Until it is read, nothing is ranked: a client whose only
 * problem is late work would read as on track (absent means unknown, never false).
 */
export type DeliveryRead = "off" | "loading" | "ready" | "error";

export function StartHere({
    clients,
    onTrack,
    onOpen,
    onAct,
    delivery,
    onRetry,
}: {
    delivery: DeliveryRead;
    /** Reads Delivery again after a failed read. */
    onRetry: () => void;
    /** Clients with at least one reason, already ranked. */
    clients: FocusClient[];
    /** Clients with none. */
    onTrack: { id: string; name: string; logo: string | null; leadTone: Tone; leadIcon: LucideIcon }[];
    onOpen: (id: string, view?: View) => void;
    /** The row's own action (the Next card's verb). */
    onAct: (id: string) => void;
}) {
    const [all, setAll] = useState(false);
    const shown = all ? clients : clients.slice(0, SHOW);
    const reasons = clients.reduce((n, c) => n + c.focuses.length, 0);
    if (delivery === "loading") {
        return (
            <section aria-labelledby="start-here" aria-busy="true" className={cn(SURFACE.card, "flex flex-col p-5")}>
                <h2 id="start-here" className={cn(TYPE.tt, "text-gray-900")}>Start here</h2>
                <p className={cn(TYPE.tc, "mt-3 text-gray-500")}>Checking Delivery before ranking clients…</p>
                <div aria-hidden className="mt-4 flex flex-col gap-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-10 animate-pulse rounded-xl bg-gray-100" />
                    ))}
                </div>
            </section>
        );
    }
    return (
        <section aria-labelledby="start-here" className={cn(SURFACE.card, "flex flex-col p-5")}>
            {delivery === "error" && (
                <div role="alert" className={cn(TYPE.tc, "mb-3 flex flex-wrap items-center gap-x-2 rounded-xl bg-rose-50 px-3 py-2 text-rose-700")}>
                    Couldn't read Delivery, so late work is not counted yet.
                    <button type="button" onClick={onRetry} className={cn("rounded font-medium underline", FOCUS_RING)}>
                        Try again
                    </button>
                </div>
            )}
            <div className="flex items-baseline justify-between gap-3">
                <h2 id="start-here" className={cn(TYPE.tt, "text-gray-900")}>Start here</h2>
                <span className={cn(TYPE.tc, "text-gray-500")}>
                    {clients.length === 0 ? "Nothing needs you" : `${clients.length} ${clients.length === 1 ? "client" : "clients"}, ${reasons} ${reasons === 1 ? "thing" : "things"}, worst first`}
                </span>
            </div>
            {clients.length === 0 ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2Icon aria-hidden size={18} className="text-emerald-600" />
                    Every client is on track.
                </div>
            ) : (
                <ol className="list-plain mt-3 flex flex-col">
                    {shown.map((c, i) => {
                        const [top, ...rest] = c.focuses;
                        return (
                            <li key={c.id} data-focus-row={c.id} className={cn("group relative flex items-center gap-3 py-3", i > 0 && "border-t border-gray-100")}>
                                <span
                                    aria-hidden
                                    className={cn(
                                        "grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-semibold tabular-nums",
                                        i === 0 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600",
                                    )}
                                >
                                    {i + 1}
                                </span>
                                <LogoSquare src={c.logo} tone={c.leadTone} icon={c.leadIcon} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <button
                                            type="button"
                                            onClick={() => onOpen(c.id, top.view)}
                                            className={cn("truncate rounded text-left text-sm font-semibold text-gray-900 hover:text-blue-600", FOCUS_RING)}
                                        >
                                            {top.title}
                                        </button>
                                        <span className={cn(TYPE.tc, "text-gray-500")}>·</span>
                                        <span className={cn(TYPE.tc, "truncate text-gray-700")}>{c.name}</span>
                                    </div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <Why f={top} />
                                        {rest.slice(0, 2).map((f) => (
                                            <Tag key={f.kind} tone={f.tone} className="!py-0">
                                                {f.why}
                                            </Tag>
                                        ))}
                                        {rest.length > 2 && <span className={cn(TYPE.tm, "text-gray-500")}>+{rest.length - 2} more</span>}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    aria-label={`${top.title}, ${c.name}`}
                                    onClick={() => (top.rowAction ? onAct(c.id) : onOpen(c.id, top.view))}
                                    className={cn(
                                        "inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-[13px] font-medium transition",
                                        i === 0 ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-100 text-gray-900 hover:bg-gray-200",
                                        FOCUS_RING,
                                    )}
                                >
                                    {top.rowAction ? top.title : "Open"}
                                    <ArrowRightIcon aria-hidden size={14} />
                                </button>
                            </li>
                        );
                    })}
                </ol>
            )}
            {clients.length > SHOW && (
                <button type="button" onClick={() => setAll((v) => !v)} className={cn(TYPE.tc, "mt-1 self-start rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                    {all ? "Show fewer" : `Show ${clients.length - SHOW} more`}
                </button>
            )}
            {onTrack.length > 0 && (
                <div className="mt-auto flex items-center gap-2 border-t border-gray-100 pt-3">
                    <span className="flex -space-x-1.5">
                        {onTrack.slice(0, 6).map((c) => (
                            <span key={c.id} className="rounded-[10px] ring-2 ring-white">
                                <LogoSquare src={c.logo} tone={c.leadTone} icon={c.leadIcon} />
                            </span>
                        ))}
                    </span>
                    <span className={cn(TYPE.tc, "text-gray-600")}>
                        <span className="font-medium text-emerald-700">{onTrack.length} on track:</span> {onTrack.map((c) => c.name).join(", ")}
                    </span>
                </div>
            )}
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * Where the scores sit
 * ------------------------------------------------------------------------- */

const BAND_DOT: Record<ReturnType<typeof band>, string> = {
    low: "ring-rose-400",
    mid: "ring-amber-400",
    good: "ring-emerald-500",
};

interface Pin {
    id: string;
    name: string;
    logo: string | null;
    /** Drawn when the site has no logo, as the table's lead square does. */
    icon: LucideIcon;
    v: number;
}

/** The client's own logo in the pin; its lead icon when there is none or it fails to load. */
function PinFace({ logo, icon: Icon }: { logo: string | null; icon: LucideIcon }) {
    const [broken, setBroken] = useState<string | null>(null);
    if (!logo || broken === logo) return <Icon aria-hidden size={14} className="text-gray-500" />;
    return <img src={logo} alt="" loading="lazy" className="h-full w-full object-contain" referrerPolicy="no-referrer" onError={() => setBroken(logo)} />;
}

/** Pins too close to share a lane are lifted to the next one, so every logo stays visible. */
function lanes(pins: Pin[]): (Pin & { lane: number })[] {
    const sorted = [...pins].sort((a, b) => a.v - b.v);
    const lastIn: number[] = [];
    return sorted.map((p) => {
        let lane = 0;
        while (lastIn[lane] !== undefined && p.v - lastIn[lane] < 7) lane++;
        lastIn[lane] = p.v;
        return { ...p, lane };
    });
}

function Track({ label, pins, onOpen }: { label: string; pins: Pin[]; onOpen: (id: string) => void }) {
    const placed = lanes(pins);
    const depth = Math.max(1, ...placed.map((p) => p.lane + 1));
    const avg = pins.length ? Math.round(pins.reduce((n, p) => n + p.v, 0) / pins.length) : null;
    const counts = { low: 0, mid: 0, good: 0 };
    for (const p of pins) counts[band(p.v)]++;
    return (
        <div>
            <div className="flex items-baseline justify-between gap-2">
                <span className={TYPE.lab}>{label}</span>
                <span className={cn(TYPE.tc, "text-gray-500")}>
                    {avg === null ? "Nothing scored" : (
                        <>
                            Avg <span className="font-semibold tabular-nums text-gray-900">{avg}</span>
                            <span className="mx-1.5 text-gray-300">|</span>
                            <span className="text-rose-600">{counts.low} low</span>, <span className="text-amber-700">{counts.mid} mid</span>, <span className="text-emerald-700">{counts.good} good</span>
                        </>
                    )}
                </span>
            </div>
            <div className="relative mt-2" style={{ height: depth * 34 + 14 }}>
                {placed.map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        title={`${p.name}: ${p.v}`}
                        aria-label={`${p.name}, ${label} ${p.v}`}
                        onClick={() => onOpen(p.id)}
                        className={cn(
                            "absolute grid h-[30px] w-[30px] -translate-x-1/2 place-items-center overflow-hidden rounded-full bg-white p-[3px] shadow-sm ring-2 transition hover:z-10 hover:scale-125",
                            BAND_DOT[band(p.v)],
                            FOCUS_RING,
                        )}
                        style={{ left: `${p.v}%`, bottom: 14 + p.lane * 34 }}
                    >
                        <PinFace logo={p.logo} icon={p.icon} />
                    </button>
                ))}
                {/* 0-100, rose under 50, amber to 70, green above: the table's own bands. */}
                <div aria-hidden className="absolute inset-x-0 bottom-0 flex h-1.5 overflow-hidden rounded-full">
                    <span className="h-full w-1/2 bg-rose-200" />
                    <span className="h-full w-[20%] bg-amber-200" />
                    <span className="h-full flex-1 bg-emerald-200" />
                </div>
                {avg !== null && <span aria-hidden className="absolute bottom-[-3px] h-3 w-0.5 -translate-x-1/2 rounded bg-gray-900" style={{ left: `${avg}%` }} />}
            </div>
            <div aria-hidden className={cn(TYPE.tm, "mt-1 flex justify-between tabular-nums text-gray-400")}>
                <span>0</span>
                <span className="ml-[40%]">50</span>
                <span className="ml-[10%]">70</span>
                <span>100</span>
            </div>
        </div>
    );
}

export function ScoreSpread({
    rows,
    onOpen,
}: {
    rows: { id: string; name: string; logo: string | null; icon: LucideIcon; aeo: number | null; seo: number | null }[];
    onOpen: (id: string) => void;
}) {
    const aeo = rows.filter((r) => r.aeo !== null).map((r) => ({ id: r.id, name: r.name, logo: r.logo, icon: r.icon, v: r.aeo as number }));
    const seo = rows.filter((r) => r.seo !== null).map((r) => ({ id: r.id, name: r.name, logo: r.logo, icon: r.icon, v: r.seo as number }));
    return (
        <section aria-labelledby="score-spread" className={cn(SURFACE.card, "flex flex-col gap-5 p-5")}>
            <h2 id="score-spread" className={cn(TYPE.tt, "text-gray-900")}>Where scores sit</h2>
            <Track label="AEO score" pins={aeo} onOpen={onOpen} />
            <Track label="Site health" pins={seo} onOpen={onOpen} />
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * Delivery, as the Tracker reads it
 * ------------------------------------------------------------------------- */

const HEALTH_BAR: Record<Health, string> = {
    at_risk: "bg-rose-500",
    slipping: "bg-amber-400",
    on_track: "bg-emerald-500",
    not_started: "bg-gray-300",
};
const HEALTH_ORDER: Health[] = ["at_risk", "slipping", "on_track", "not_started"];

export function DeliveryHealth({
    cards,
    unread,
    status,
    onRetry,
}: {
    cards: ClientCard[];
    /** Clients on this tab whose own Delivery read failed: unknown, never counted as absent. */
    unread: string[];
    status: "loading" | "ready" | "error";
    onRetry: () => void;
}) {
    const total = cards.length;
    const unreadNote = unread.length > 0 && (
        <p role="alert" className={cn(TYPE.tc, "mt-3 text-rose-700")}>
            Couldn't read {unread.length === 1 ? "1 client" : `${unread.length} clients`}: {unread.join(", ")}.{" "}
            <button type="button" onClick={onRetry} className={cn("rounded font-medium underline", FOCUS_RING)}>
                Try again
            </button>
        </p>
    );
    const by = (h: Health) => cards.filter((c) => c.health === h);
    const late = cards.reduce((n, c) => n + c.late.length, 0);
    return (
        <section aria-labelledby="delivery-health" className={cn(SURFACE.card, "flex flex-col p-5")}>
            <div className="flex items-baseline justify-between gap-2">
                <h2 id="delivery-health" className={cn(TYPE.tt, "text-gray-900")}>Delivery</h2>
                <Link to={`${OURA_ROOT}/tracker`} className={cn(TYPE.tc, "inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                    Open Tracker <ArrowRightIcon aria-hidden size={13} />
                </Link>
            </div>
            {status === "loading" ? (
                <div className="mt-4 h-3 animate-pulse rounded-full bg-gray-100" />
            ) : status === "error" ? (
                <p role="alert" className={cn(TYPE.tc, "mt-3 text-rose-700")}>
                    Couldn't read Delivery.{" "}
                    <button type="button" onClick={onRetry} className={cn("rounded font-medium underline", FOCUS_RING)}>
                        Try again
                    </button>
                </p>
            ) : total === 0 ? (
                unreadNote || <p className={cn(TYPE.tc, "mt-3 text-gray-500")}>No Delivery to read yet.</p>
            ) : (
                <>
                    <p className={cn(TYPE.tc, "mt-1 text-gray-500")}>
                        <span className={cn("font-semibold tabular-nums", late ? "text-rose-600" : "text-gray-900")}>{late}</span> {late === 1 ? "item" : "items"} past its date across {total} {total === 1 ? "client" : "clients"}
                        {unread.length > 0 && " read so far"}
                    </p>
                    {unreadNote}
                    <div aria-hidden className="mt-3 flex h-3 gap-0.5 overflow-hidden rounded-full">
                        {HEALTH_ORDER.map((h) => {
                            const n = by(h).length;
                            return n ? <span key={h} className={HEALTH_BAR[h]} style={{ flexGrow: n }} /> : null;
                        })}
                    </div>
                    <ul className="list-plain mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                        {HEALTH_ORDER.map((h) => {
                            const list = by(h);
                            return (
                                <li key={h} className="min-w-0" title={list.map((c) => c.client.name).join(", ") || undefined}>
                                    <span className="flex items-center gap-2 text-sm">
                                        <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", HEALTH_BAR[h])} />
                                        <span className="text-gray-700">{HEALTH[h].label}</span>
                                        <span className="ml-auto font-semibold tabular-nums text-gray-900">{list.length}</span>
                                    </span>
                                    {list.length > 0 && h !== "on_track" && (
                                        <span className={cn(TYPE.tm, "block truncate pl-4 text-gray-500")}>{list.map((c) => c.client.name).join(", ")}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </section>
    );
}

/* ------------------------------------------------------------------------- *
 * Table pieces
 * ------------------------------------------------------------------------- */

/** The term as one bar, filled to the month we are in; amber once the renewal window opens. */
export function TermBar({ month, of, endsSoon }: { month: number; of: number; endsSoon: boolean }) {
    return (
        <span className="inline-flex w-24 flex-col gap-1">
            <span className="text-sm tabular-nums text-gray-700">
                Month {month} <span className="text-gray-400">of {of}</span>
            </span>
            <span aria-hidden className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
                <span className={cn("block h-full rounded-full", endsSoon ? "bg-amber-400" : "bg-gray-400")} style={{ width: `${Math.min(100, (month / of) * 100)}%` }} />
            </span>
        </span>
    );
}

/** A score as its number over a short bar in its band colour. */
export function ScoreBar({ v }: { v: number }) {
    const b = band(v);
    return (
        <span className="inline-flex w-12 flex-col items-center gap-1">
            <span className="text-sm font-semibold tabular-nums text-gray-900">{v}</span>
            <span aria-hidden className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
                <span className={cn("block h-full rounded-full", b === "good" ? "bg-emerald-500" : b === "mid" ? "bg-amber-400" : "bg-rose-400")} style={{ width: `${Math.max(4, v)}%` }} />
            </span>
        </span>
    );
}

/** The row's top reason, under its "Next up" head. */
export function NextUp({
    focuses,
    onPress,
    checking,
}: {
    focuses: Focus[];
    /** Does the top reason: the row's action, or opens the client where it is. */
    onPress: (f: Focus) => void;
    /** Delivery is still being read: no reason yet is not "on track" yet. */
    checking?: boolean;
}) {
    if (!focuses.length && checking) return <span className={cn(TYPE.tc, "text-gray-400")}>Checking…</span>;
    if (!focuses.length) {
        return (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                <CheckCircle2Icon aria-hidden size={15} /> On track
            </span>
        );
    }
    const [top, ...rest] = focuses;
    return (
        <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", top.tone === "blue" ? "bg-blue-500" : top.tone === "red" || top.tone === "rose" ? "bg-rose-500" : "bg-amber-500")} />
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPress(top);
                    }}
                    className={cn("truncate rounded text-left hover:text-blue-600 hover:underline", top.rowAction && "text-blue-600", FOCUS_RING)}
                >
                    {top.title}
                </button>
                {rest.length > 0 && <span className={cn(TYPE.tm, "shrink-0 rounded-full bg-gray-100 px-1.5 text-gray-600")}>+{rest.length}</span>}
            </span>
            <span className={cn(TYPE.tc, "truncate pl-3.5", top.late ? "font-medium text-rose-600" : "text-gray-500")}>{top.why}</span>
        </span>
    );
}
